// Uncertainty, kept honest.
//
// Extraction gets things wrong: an ingredient with no amount beside it, a step
// that reads as half a sentence. The rule for this app is that we say so
// rather than invent a plausible number. These helpers decide what counts as
// missing, and they are shared by the extractor (which sets the flags) and the
// Recipe screen (which counts and clears them) so the two can't disagree.
//
// NOT a mock. Real extraction produces the same uncertainty; only who reports
// it changes.

// An amount can be a phrase rather than a number. "Salt, to taste" is a
// complete instruction — the recipe is not missing anything, and flagging it
// would train the user to ignore the flags.
const VAGUE_BUT_INTENTIONAL = [
  /^to taste$/i,
  /^as needed$/i,
  /^as desired$/i,
  /^for (serving|garnish|frying|dusting|drizzling|the pan)$/i,
  /^a (pinch|dash|splash|handful|drizzle|few)\b/i,
  /^(pinch|dash|splash|handful|drizzle)\b/i,
  /^optional$/i,
];

export function isQuantityMissing(quantity) {
  const text = String(quantity ?? '').trim();
  if (!text) return true;
  return !VAGUE_BUT_INTENTIONAL.some((pattern) => pattern.test(text));
}

// Touching the field is the user answering the question, so the flag comes off
// and stays off — even if they clear it again afterwards. The flag means "we
// couldn't read this", not "this is empty", and once they've looked at the row
// that is no longer true.
export function clearQuantityFlag(item) {
  return item.quantityMissing ? { ...item, quantityMissing: false } : item;
}

export function clearStepFlag(step) {
  return step.unclear ? { ...step, unclear: false } : step;
}

export function countFlags({ ingredients = [], steps = [] } = {}) {
  const missingQuantities = ingredients.filter((item) => item.quantityMissing).length;
  const unclearSteps = steps.filter((step) => step.unclear).length;
  return { missingQuantities, unclearSteps, total: missingQuantities + unclearSteps };
}

export function describeFlags(counts) {
  if (!counts.total) return null;
  const parts = [];
  if (counts.missingQuantities) {
    parts.push(
      `${counts.missingQuantities} ${counts.missingQuantities === 1 ? 'amount' : 'amounts'} we couldn't read`
    );
  }
  if (counts.unclearSteps) {
    parts.push(`${counts.unclearSteps} unclear ${counts.unclearSteps === 1 ? 'step' : 'steps'}`);
  }
  return parts.join(' and ');
}
